import { useCallback, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Column } from '@tanstack/react-table';

// Module-level variable to track which filter input should have focus
// This persists across component unmount/remount cycles
let activeFilterId: string | undefined = undefined;

interface ColumnFilterInputProps<TData> {
	column: Column<TData, unknown>;
	placeholder?: string;
	type?: 'text' | 'date';
	className?: string;
}

export function ColumnFilterInput<TData>({
	column,
	placeholder,
	type = 'text',
	className = 'h-8',
}: ColumnFilterInputProps<TData>) {
	const inputRef = useRef<HTMLInputElement>(null);
	// Use column.id as the unique identifier for this filter input
	const filterId = column.id;

	// Restore focus if this input was the active one before unmount/remount
	useEffect(() => {
		if (activeFilterId === filterId && inputRef.current) {
			inputRef.current.focus();
			// Reclaim the activeFilterId to prevent the delayed blur from clearing it
			activeFilterId = filterId;
		}
	});

	const handleChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;

			if (type === 'date') {
				if (value) {
					// Convert to ISO string format for date inputs
					const isoString = `${value}T00:00:00.000Z`;
					column.setFilterValue(isoString);
				} else {
					column.setFilterValue(null);
				}
			} else {
				column.setFilterValue(value || null);
			}
		},
		[column, type],
	);

	const handleFocus = useCallback(() => {
		activeFilterId = filterId;
	}, [filterId]);

	const handleBlur = useCallback(() => {
		// Delay clearing to allow new component to mount and reclaim focus
		// This handles the case where blur fires during unmount/remount cycle
		setTimeout(() => {
			if (activeFilterId === filterId) {
				activeFilterId = undefined;
			}
		}, 100);
	}, [filterId]);

	// Get the display value based on input type
	const getDisplayValue = () => {
		const filterValue = column.getFilterValue() as string;
		if (!filterValue) return '';

		if (type === 'date') {
			// Convert ISO string back to date input format (YYYY-MM-DD)
			return new Date(filterValue).toISOString().split('T')[0];
		}
		return filterValue;
	};

	return (
		<Input
			ref={inputRef}
			type={type}
			placeholder={placeholder}
			value={getDisplayValue()}
			onChange={handleChange}
			onFocus={handleFocus}
			onBlur={handleBlur}
			className={className}
			onClick={(e) => e.stopPropagation()}
		/>
	);
}
